"use client";

import { createClient } from "../supabase/client";
import { pendingResolutionError, type PendingResolution, type PendingTask } from "../pendingIssues";
import type { Database } from "../../types/database";

export interface PendingInventory {
  tasks: PendingTask[];
  projects: { id: string; name: string }[];
  labels: { id: string; name: string }[];
  owners: { id: string; name: string }[];
}
export class PendingDataError extends Error {
  constructor(readonly code: "invalid_input" | "unauthorized" | "session_changed" | "aborted" | "query_failed" | "invalid_response" | "changed" | "forbidden") {
    super(`Pending issues: ${code}`); this.name = "PendingDataError";
  }
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const PENDING_PAGE_SIZE = 500;
const COLUMNS = "id,identifier,workspace_id,team_id,title,status,priority,assignee_id,owner_id,executor_agent_id,project_id,created_at,updated_at,deleted_at,issue_labels(label_id),comments!comments_issue_id_fkey(updated_at)";
const PRIORITY = { none: 0, urgent: 1, high: 2, medium: 3, low: 4 } as const;
type Client = ReturnType<typeof createClient>;
type IssueRow = Pick<Database["public"]["Tables"]["issues"]["Row"], "id" | "identifier" | "workspace_id" | "team_id" | "title" | "status" | "priority" | "assignee_id" | "owner_id" | "executor_agent_id" | "project_id" | "created_at" | "updated_at" | "deleted_at"> & {
  issue_labels: { label_id: string }[]; comments: { updated_at: string }[];
};
type Reply<T> = { data: T[] | null; error: unknown; count: number | null };
function fail(code: PendingDataError["code"]): never { throw new PendingDataError(code); }
function checkIds(...ids: string[]) { if (ids.some((id) => !UUID.test(id))) fail("invalid_input"); }
function timestamp(value: string): number {
  const parsed = Date.parse(value); if (!Number.isFinite(parsed)) fail("invalid_response"); return parsed;
}

async function withContext<T>(workspaceId: string, userId: string, signal: AbortSignal | undefined,
  work: (client: Client, check: () => void) => Promise<T>): Promise<T> {
  checkIds(workspaceId, userId);
  const client = createClient();
  let changed = false;
  const check = () => { if (signal?.aborted) fail("aborted"); if (changed) fail("session_changed"); };
  const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
    if (session?.user.id !== userId) changed = true;
  });
  async function principal() {
    check();
    const auth = await client.auth.getUser();
    check();
    if (auth.error || auth.data.user?.id !== userId) fail("unauthorized");
    const member = await client.from("workspace_members").select("workspace_id,user_id,role")
      .eq("workspace_id", workspaceId).eq("user_id", userId).maybeSingle();
    check();
    if (member.error) fail("query_failed");
    if (member.data?.workspace_id !== workspaceId || member.data.user_id !== userId || ["bot", "bot_readonly"].includes(member.data.role)) fail("forbidden");
  }
  try {
    await principal();
    const result = await work(client, check);
    await principal();
    return result;
  } catch (error) { if (error instanceof PendingDataError) throw error; check(); fail("query_failed"); }
  finally { subscription.unsubscribe(); }
}

/** Count + actual page length handles a server cap lower than our requested range. */
async function pages<T>(query: (from: number, to: number) => PromiseLike<Reply<T>>, check: () => void): Promise<T[]> {
  const rows: T[] = [];
  let expected: number | null = null;
  for (;;) {
    check();
    const result = await query(rows.length, rows.length + PENDING_PAGE_SIZE - 1);
    check();
    if (result.error) fail("query_failed");
    if (result.count !== null && (!Number.isSafeInteger(result.count) || result.count < 0)) fail("invalid_response");
    if (expected !== null && result.count !== expected) fail("changed");
    expected = result.count;
    const batch = result.data ?? [];
    rows.push(...batch);
    if (expected !== null && rows.length >= expected) {
      if (rows.length !== expected) fail("changed");
      return rows;
    }
    if (!batch.length) { if (expected !== null) fail("changed"); return rows; }
  }
}
function distinct<T>(rows: readonly T[], id: (row: T) => string) {
  const seen = new Set<string>();
  for (const row of rows) { const value = id(row); if (!UUID.test(value) || seen.has(value)) fail("invalid_response"); seen.add(value); }
}

export async function loadPendingIssues(workspaceId: string, userId: string, signal?: AbortSignal): Promise<PendingInventory> {
  return withContext(workspaceId, userId, signal, async (client, check) => {
    const [rows, projects, labels, members] = await Promise.all([
      pages<IssueRow>((from, to) => {
        let query = client.from("issues").select(COLUMNS, { count: "exact" }).eq("workspace_id", workspaceId)
          .is("deleted_at", null).not("status", "in", "(done,canceled)").order("id").range(from, to)
          .order("updated_at", { referencedTable: "comments", ascending: false }).limit(1, { referencedTable: "comments" });
        if (signal) query = query.abortSignal(signal);
        return query;
      }, check),
      pages((from, to) => {
        let query = client.from("projects").select("id,name,workspace_id", { count: "exact" }).eq("workspace_id", workspaceId).order("id").range(from, to);
        if (signal) query = query.abortSignal(signal); return query;
      }, check),
      pages((from, to) => {
        let query = client.from("labels").select("id,name,teams!inner(workspace_id)", { count: "exact" })
          .eq("teams.workspace_id", workspaceId).order("id").range(from, to);
        if (signal) query = query.abortSignal(signal); return query;
      }, check),
      pages((from, to) => {
        let query = client.from("workspace_members").select("workspace_id,user_id,role", { count: "exact" })
          .eq("workspace_id", workspaceId).order("user_id").range(from, to);
        if (signal) query = query.abortSignal(signal); return query;
      }, check),
    ]);
    distinct(rows, (row) => row.id); distinct(projects, (row) => row.id); distinct(labels, (row) => row.id); distinct(members, (row) => row.user_id);
    if (projects.some((row) => row.workspace_id !== workspaceId) || labels.some((row) => row.teams?.workspace_id !== workspaceId) || members.some((row) => row.workspace_id !== workspaceId)) fail("invalid_response");
    const memberIds = members.filter((row) => !["bot", "bot_readonly"].includes(row.role)).map((row) => row.user_id);
    const owners: PendingInventory["owners"] = [];
    for (let offset = 0; offset < memberIds.length; offset += 100) {
      const ids = memberIds.slice(offset, offset + 100);
      const profiles = await pages((from, to) => {
        let query = client.from("profiles").select("id,name,is_bot", { count: "exact" }).in("id", ids).eq("is_bot", false).order("id").range(from, to);
        if (signal) query = query.abortSignal(signal); return query;
      }, check);
      if (profiles.some((row) => !ids.includes(row.id) || row.is_bot !== false)) fail("invalid_response");
      owners.push(...profiles.map(({ id, name }) => ({ id, name })));
    }
    const tasks = rows.map((row): PendingTask => {
      if (row.workspace_id !== workspaceId || row.deleted_at !== null || !["todo", "backlog", "in_progress", "in_review"].includes(row.status) || PRIORITY[row.priority] === undefined || !Array.isArray(row.issue_labels) || !Array.isArray(row.comments)) fail("invalid_response");
      const updatedAt = timestamp(row.updated_at);
      return { id: row.id, identifier: row.identifier, workspaceId: row.workspace_id, teamId: row.team_id,
        title: row.title, status: row.status, priority: PRIORITY[row.priority], ownerId: row.owner_id,
        assigneeId: row.assignee_id, executorAgentId: row.executor_agent_id, projectId: row.project_id,
        labelIds: row.issue_labels.map((label) => label.label_id), createdAt: timestamp(row.created_at), updatedAt,
        version: row.updated_at, lastActivityAt: Math.max(updatedAt, ...row.comments.map((comment) => timestamp(comment.updated_at))) };
    });
    return { tasks, projects: projects.map(({ id, name }) => ({ id, name })), labels: labels.map(({ id, name }) => ({ id, name })), owners };
  });
}

export async function resolvePendingIssue(workspaceId: string, userId: string, resolution: PendingResolution, signal?: AbortSignal): Promise<void> {
  checkIds(resolution.issueId, ...(resolution.destinationIssueId ? [resolution.destinationIssueId] : []));
  if (pendingResolutionError(resolution) || !Number.isFinite(Date.parse(resolution.version))) fail("invalid_input");
  return withContext(workspaceId, userId, signal, async (client, check) => {
    check();
    let query = client.rpc("resolve_pending_issue", {
      p_workspace_id: workspaceId, p_actor_id: userId, p_issue_id: resolution.issueId,
      p_expected_updated_at: resolution.version, p_action: resolution.action, p_reason: resolution.reason.trim(),
      p_destination_issue_id: resolution.action === "duplicate" ? resolution.destinationIssueId ?? null : null,
      p_evidence: resolution.action === "done" ? resolution.evidence?.trim() ?? null : null,
    });
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    check();
    if (error) fail(error.code === "40001" ? "changed" : error.code === "42501" ? "forbidden" : "query_failed");
    if (data !== resolution.issueId) fail("invalid_response");
  });
}
